import { TmapApp } from '@tmap-web-lib/tmap-app-interface'
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react'
import { Container, LoadingGlobalIndicator, ModalMainError } from './components'
import { RoutePath, CheckPageId } from '@/routes'
import { useAppStore } from '@/store'
import { clearError } from '@/store/app'
import { GlobalModalManagerProvider, PageIdContextProvider, TmapLogContextProvider } from '@/providers'
import { Outlet, useLocation } from 'react-router-dom'
import { ErrorBoundary } from 'react-error-boundary'
import { AxiosError, isCancel } from 'axios'
import { lazyRetry } from '@/utils/lazy-retry'
import { useCommonErrorLogs } from '@/hooks'
import { useModalManager } from '@tmap-web-lib/modal-manager'
import { ModalAlert } from '@/components/tmds/Modal/ModalAlert'

const DevTools = lazyRetry(() => import('./dev-tools/LifeTabDevTools'))
const controller = new AbortController()
const CIRCULAR = `${import.meta.env.VITE_CDN_URL}/common/images/loading/circular_spin.svg`

interface Props {
  isPrd: boolean;
}

function App({ isPrd }: Props) {
  const location = useLocation()
  const commonErrorLogs = useCommonErrorLogs()

  const [error, setError] = useState<AxiosError>()

  const bgStyle = useMemo(() => {
    const path = location.pathname

    switch (path) {
      case RoutePath.Home:
        return 'main'
      case RoutePath.History:
      case RoutePath.Benefit_detail:
      case RoutePath.Benefit_home:
        return 'sub'
      default:
        return undefined
    }
  }, [location])

  const handleError = useCallback((error: AxiosError) => {
    setError((prev) => {
      if (!prev) return error
      if (prev.response?.status !== error.response?.status) return error
      return prev
    })

    // 네트워크 에러 및 API Request aborted 에러는 모니터링 로그에서 제외 (TMAPWEB-4966)
    const isNetworkError = error.code === 'ERR_NETWORK'
    const isAbortedError = isCancel(error) || error.code === 'ECONNABORTED' || error.code === 'ERR_CANCELED'

    if (isNetworkError || isAbortedError) {
      return
    }

    const statusCode = error?.response?.status
    const displayMessage = statusCode === 401
      ? '사용자 인증정보가 변경되었습니다.'
      : '일시적으로 서비스를 이용할 수 없습니다.'

    commonErrorLogs({
      displayMessage,
      apiPath: error.config?.url,
      errorCode: error.code,
      statusCode,
      errorMessage: error.message,
      error: error.response?.data as Record<string, unknown>,
    })
  }, [commonErrorLogs])

  useEffect(() => {
    return TmapApp.utils.addNativeEventListener('onPause', () => {
      if (controller.signal) {
        controller.abort()
      }
    })
  }, [])

  useEffect(() => {
    return TmapApp.utils.addNativeEventListener('onRefresh', () => {
      window.location.reload()
    })
  }, [])

  useEffect(() => {
    return TmapApp.utils.addNativeEventListener('onResume', () => {
      const bodyEl = document.querySelector('body') as HTMLBodyElement
      const element = document.createElement('div')
      element.style.cssText = 'position: absolute; left: -10px; width: 1px; height: 1px; background: rgba(0, 0, 0, 0.01)'
      bodyEl.appendChild(element)
      setTimeout(() => (element?.remove()), 1000)
    })
  }, [])

  // 닉네임을 서버에서 임으로 생성 앱은 바로 갱신되지 않는 이유로 웹에서 단말 설정 정보 갱신 요청(TMAPWEB-3697)
  useEffect(() => {
    TmapApp.getUserAllSettings()
      .then((data) => {
        if (!data?.settings.user.name) {
          TmapApp.notifyChangedInfo({ type: 'personalProfile' })
        }
      })
  }, [])

  return (
    <>
      <LoadingGlobalIndicator
        rootElementSelector={'#root'}
        imageSrc={CIRCULAR}
        useDelay={false}
      />

      <PageIdContextProvider>
        <TmapLogContextProvider>
          <GlobalModalManagerProvider>
            <ErrorBoundary
              onError={(error) => handleError(error as AxiosError)}
              fallback={<ErrorNotifier error={error}/>}
            >
              <CheckPageId>
                <Container className={bgStyle}>
                  <Suspense fallback={''}>
                    <Outlet />
                  </Suspense>
                </Container>
              </CheckPageId>
            </ErrorBoundary>
          </GlobalModalManagerProvider>
        </TmapLogContextProvider>
      </PageIdContextProvider>
      {(!isPrd && !error) && <Suspense fallback=""><DevTools/></Suspense>}
    </>
  )
}

export function ErrorNotifier({ error }: { error: AxiosError | undefined }) {
  const store = useAppStore()
  const modalManager = useModalManager()
  const { pathname } = useLocation()

  useEffect(() => {
    if (!error) return

    const DEFAULT_MESSAGE = '잠시 후 다시 시도해주세요.'
    const isProd = import.meta.env.VITE_BUILD_ENV === 'PRD'

    const code = error?.response?.status
    const title = code === 401
      ? '사용자 인증정보가 변경되었습니다.'
      : '일시적으로 서비스를\n이용할 수 없습니다.'
    const debug =
      !isProd && (error?.code || error?.message || error?.response?.data)
        ? `\n${error.code ?? ''} ${error.message ?? ''}\n${JSON.stringify(error.response?.data ?? {}, null, 2)}`
        : ''

    const detail = code === 401 ? DEFAULT_MESSAGE : `${DEFAULT_MESSAGE}${debug}`

    const onClose = () => {
      store.dispatch(clearError())
      if (pathname === RoutePath.Home) {
        window.location.reload()
      } else {
        TmapApp.onBackKeyPressed()
      }
    }

    const raf = requestAnimationFrame(() => {
      modalManager.showModal({
        render: ({ close }) =>
          pathname === RoutePath.Home
            ? <ModalMainError title={title} detail={detail} onReload={close} />
            : <ModalAlert title={title} detail={detail} onOk={close} />,
        onClosed: () => onClose(),
      })
    })
    return () => cancelAnimationFrame(raf)
  }, [error, modalManager, pathname, store])

  return null
}

export default App
